import axios, { HttpStatusCode } from "axios";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  buildReportWhereDescriptor,
  errors,
  isEmptyArray,
  isEmptyObject,
  isEmptyOrBlankStringArray,
  isOperatorReportFilterType,
  Log,
  normalizeErrors,
  reportDataAF,
  reportFiltersWithValuesAF,
  reportFlexGroupingParamsAF,
  reportGroupingPeriodAF,
  reportLastSearchCriteriaByReportIdAF,
  ReportsApi,
  reportsTableColumnsOrderStateAF,
  reportsTableColumnsVisibilityAF,
  SimpleResult,
  toForbiddenTranslatableItem,
  topCalendarDatesAtomFamily,
  trimSeconds,
  type ApiException,
  type IApiError,
  type IReportCriteriaApi,
  type IReportCriteriaFilters,
  type IReportCriteriaWhere,
  type TError,
} from "@ryde/shared";

import { usePageContext } from "../../contexts";
import { useReportsActionsUtils } from "./use-report-actions.utils";
import { useReportId } from "./use-report-id";

interface IFetchReportsOptions {
  abortController?: AbortController;
}

export const useReportActions = () => {
  const { t, i18n } = useTranslation();
  const { entity } = usePageContext();
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const { reportId } = useReportId();
  const filterObject = useRecoilValue(reportFiltersWithValuesAF(reportId));
  const storedReportApiDto = useRecoilValue(reportDataAF(reportId));
  const periodGrouping = useRecoilValue(reportGroupingPeriodAF(reportId));
  const groupingParams = useRecoilValue(reportFlexGroupingParamsAF(reportId));
  const columnOrder = useRecoilValue(reportsTableColumnsOrderStateAF(reportId));
  const columnVisibility = useRecoilValue(
    reportsTableColumnsVisibilityAF(reportId),
  );
  const setReportLastSearchCriteria = useSetRecoilState(
    reportLastSearchCriteriaByReportIdAF(reportId),
  );
  const lastReportIdRef = useRef<number | undefined>(undefined);

  const {
    setReportData,
    clearReportData,
    setReportFilterSettings,
    setReportColumnVisibility,
    setReportSchemaValues,
    setReportColumnOrderState,
    setReportPeriodGrouping,
    setReportClientSortState,
    setReportColumnsSizing,
    setReportFlexGroupingParams,
    resetReportPagination,
  } = useReportsActionsUtils();

  const fetchCriteria: IReportCriteriaApi = useMemo(() => {
    const apiCriteria: IReportCriteriaApi = {
      id: reportId,
    };
    const filters: IReportCriteriaFilters = {};
    const where: IReportCriteriaWhere = {};
    const availableFilters =
      storedReportApiDto?.reportTemplate?.availableFilters;

    if (calendarDates) {
      filters.dates = {
        value: [
          trimSeconds(calendarDates.date1),
          trimSeconds(calendarDates.date2),
        ],
      };
    }

    for (const [filterKey, filterValue] of Object.entries(filterObject)) {
      if (
        filterValue !== undefined &&
        filterValue !== null &&
        !isEmptyArray(filterValue) &&
        !isEmptyObject(filterValue) &&
        !isEmptyOrBlankStringArray(filterValue)
      ) {
        // Operator-typed filters (number comparison/between, RYDE-8067) store an operator choice, which
        // the legacy { value } shape cannot carry — they go to criteria.where as { $op: value }. An
        // incomplete one (operator without a number, single range bound) is skipped entirely: it must
        // never leak into the legacy shape, where its encoded string would reach SQL as a literal.
        const filterFieldTypeId =
          availableFilters?.[filterKey]?.filterFieldTypeId;

        if (isOperatorReportFilterType(filterFieldTypeId)) {
          const whereDescriptor = buildReportWhereDescriptor({
            filterFieldTypeId,
            values: filterValue,
          });
          if (whereDescriptor) {
            where[filterKey] = whereDescriptor;
          }
        } else {
          filters[filterKey] = { value: filterValue };
        }
      }
    }

    apiCriteria.filters = filters;
    if (!isEmptyObject(where)) {
      apiCriteria.where = where;
    }
    apiCriteria.flexGrouping = { periodGrouping, groupingParams };
    return apiCriteria;
  }, [
    reportId,
    calendarDates,
    periodGrouping,
    groupingParams,
    filterObject,
    storedReportApiDto?.reportTemplate?.availableFilters,
  ]);

  // The visible columns in the user's chosen order, for the download-only CSV export (RYDE-7777).
  // Mirrors the settings drawer: columnOrder is the full ordered key list, kept only where the column is
  // currently visible (TanStack marks a hidden column false; unset means visible).
  const visibleColumnsOrdered = useMemo(
    () =>
      columnOrder.filter(columnKey => columnVisibility[columnKey] !== false),
    [columnOrder, columnVisibility],
  );

  const fetchReport = useEvent(
    async ({
      abortController,
    }: IFetchReportsOptions): Promise<SimpleResult<unknown>> => {
      try {
        if (!fetchCriteria?.id) {
          return new SimpleResult({ error: errors.common.invalidId });
        }

        setLoading(true);

        setReportLastSearchCriteria(fetchCriteria);

        const fetchResult = await ReportsApi.fetchReportData(
          fetchCriteria,
          abortController,
        );

        if (!fetchResult) {
          return new SimpleResult({
            error: t(errors.common.emptyApiResponse),
          });
        }

        if (!fetchResult?.success) {
          return fetchResult;
        }

        const reportApiDto = fetchResult.data;

        if (!reportApiDto) {
          return new SimpleResult({
            error: t(errors.common.invalidEntityEmptyObject),
          });
        }

        const reportFetchId = fetchCriteria.id;
        Log.debug(`Report dto received (id: ${reportFetchId}): `, reportApiDto);

        setReportData(reportFetchId, reportApiDto);
        setReportColumnOrderState({
          reportId: reportFetchId,
          reportApiDto,
          fetchCriteria,
          isSameReportRender: lastReportIdRef.current === reportId,
        });
        setReportColumnVisibility({
          reportId: reportFetchId,
          reportApiDto,
          fetchCriteria,
          isSameReportRender: lastReportIdRef.current === reportId,
        });

        setReportClientSortState(
          reportFetchId,
          reportApiDto,
          lastReportIdRef.current === reportId,
        );

        if (lastReportIdRef.current !== reportId) {
          lastReportIdRef.current = reportId;

          resetReportPagination(reportFetchId);
          setReportSchemaValues(reportFetchId, reportApiDto);
          setReportFilterSettings(reportFetchId, reportApiDto);
          setReportPeriodGrouping(reportFetchId, reportApiDto);
          setReportFlexGroupingParams(reportFetchId, reportApiDto);
          setReportColumnsSizing(reportFetchId);
        }

        return new SimpleResult({ success: true, data: reportApiDto });
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.debug("Axios, request canceled", error);
          return new SimpleResult({
            error: normalizeErrors(error as TError, t),
          });
        }

        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          // A denied fetch must never leave previously rendered rows (possibly another
          // session's data) on screen behind the error toast (RYDE-8042)
          if (fetchCriteria.id) {
            clearReportData(fetchCriteria.id);
          }
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          Log.errorAndToast(
            t(errors.reports.failedFetchingReports),
            errorDetails,
          );
          return new SimpleResult({ error: errorDetails });
        }

        Log.warnAndToast(t(errors.reports.failedFetchingReports), error);

        setErrors(normalizeErrors(error as IApiError, t));

        return new SimpleResult({
          error: normalizeErrors(error as IApiError, t),
        });
      } finally {
        setLoading(false);
      }
    },
  );

  const fetchReportCSV = useEvent(
    async ({
      abortController,
    }: IFetchReportsOptions): Promise<SimpleResult<unknown>> => {
      try {
        if (!fetchCriteria?.id) {
          return new SimpleResult({ error: errors.common.invalidId });
        }

        setLoading(true);

        // Send the current column selection/order so the server CSV contains exactly those columns
        // (RYDE-7777). Omitted when nothing is selected yet, so the server falls back to the full set.
        const csvCriteria: IReportCriteriaApi = visibleColumnsOrdered.length
          ? { ...fetchCriteria, visibleColumns: visibleColumnsOrdered }
          : fetchCriteria;

        const fetchResult = await ReportsApi.fetchReportDataCSV(
          i18n.language,
          csvCriteria,
          abortController,
        );

        if (!fetchResult) {
          return new SimpleResult({
            error: t(errors.common.emptyApiResponse),
          });
        }

        if (!fetchResult?.success) {
          return fetchResult;
        }

        return new SimpleResult({ success: true, data: fetchResult.data });
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.debug("Axios, request canceled", error);
          return new SimpleResult({
            error: normalizeErrors(error as TError, t),
          });
        }

        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          Log.errorAndToast(
            t(errors.reports.failedFetchingReports),
            errorDetails,
          );
          return new SimpleResult({ error: errorDetails });
        }

        Log.warnAndToast(t(errors.reports.failedFetchingReports), error);

        setErrors(normalizeErrors(error as IApiError, t));

        return new SimpleResult({
          error: normalizeErrors(error as IApiError, t),
        });
      } finally {
        setLoading(false);
      }
    },
  );

  return { fetchReport, fetchReportCSV, fetchCriteria };
};
